import { useCallback, useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { NSecSigner } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import {
  ensureMerchantIdentity,
  loadMerchantIdentity,
  canPublishHeartbeat,
  recordHeartbeat,
  buildMerchantListing,
  buildActivityHeartbeat,
  MERCHANT_LISTING_KIND,
  type MerchantProfile,
  type MerchantIdentity,
} from '@/lib/merchantNostr';

/**
 * Hook for publishing merchant discovery events to Nostr.
 *
 * Uses a dedicated merchant keypair (auto-generated, stored in localStorage)
 * that is separate from any Nostr login the user might have.
 */
export function useMerchantNostr() {
  const { nostr } = useNostr();

  const identity = useMemo<MerchantIdentity | null>(() => loadMerchantIdentity(), []);

  /** Get or create the signer from the merchant nsec */
  const getSigner = useCallback(() => {
    const id = ensureMerchantIdentity();
    const decoded = nip19.decode(id.nsec);
    if (decoded.type !== 'nsec') throw new Error('Invalid merchant nsec');
    return new NSecSigner(decoded.data);
  }, []);

  /** Publish the merchant listing (kind 35341) */
  const publishProfile = useCallback(async (profile: MerchantProfile) => {
    const signer = getSigner();
    const template = buildMerchantListing(profile);
    const event = await signer.signEvent({
      kind: template.kind,
      content: template.content,
      tags: template.tags,
      created_at: Math.floor(Date.now() / 1000),
    });
    await nostr.event(event, { signal: AbortSignal.timeout(10000) });
    return event;
  }, [nostr, getSigner]);

  /** Publish an activity heartbeat (kind 7531), respecting the 24h throttle */
  const publishHeartbeat = useCallback(async () => {
    if (!canPublishHeartbeat()) return null;
    const signer = getSigner();
    const template = buildActivityHeartbeat();
    const event = await signer.signEvent({
      kind: template.kind,
      content: template.content,
      tags: template.tags,
      created_at: Math.floor(Date.now() / 1000),
    });
    await nostr.event(event, { signal: AbortSignal.timeout(10000) });
    recordHeartbeat();
    return event;
  }, [nostr, getSigner]);

  /**
   * Delete the merchant listing.
   *
   * Strategy: publish an empty tombstone replacement for the addressable event
   * (relays always replace addressable events with newer ones from the same
   * pubkey+kind+d-tag), PLUS a kind 5 deletion request for relays that support it.
   */
  const deleteListing = useCallback(async () => {
    const id = loadMerchantIdentity();
    if (!id) throw new Error('No merchant identity to delete');
    const signer = getSigner();
    const now = Math.floor(Date.now() / 1000);

    // 1. Publish an empty tombstone that replaces the real listing.
    //    Omit the t=bch-merchant tag so relay queries for #t=bch-merchant
    //    won't even return it.
    const tombstone = await signer.signEvent({
      kind: MERCHANT_LISTING_KIND,
      content: '',
      tags: [
        ['d', 'bch-merchant'],
        ['deleted', 'true'],
        ['alt', 'Deleted BCH merchant listing'],
      ],
      created_at: now,
    });
    await nostr.event(tombstone, { signal: AbortSignal.timeout(15000) });

    // 2. Also publish a kind 5 deletion request (best effort)
    try {
      const aTag = `${MERCHANT_LISTING_KIND}:${id.pubkey}:bch-merchant`;
      const deletion = await signer.signEvent({
        kind: 5,
        content: 'Merchant listing removed',
        tags: [
          ['a', aTag],
          ['k', String(MERCHANT_LISTING_KIND)],
        ],
        created_at: now,
      });
      await nostr.event(deletion, { signal: AbortSignal.timeout(10000) });
    } catch {
      // Kind 5 is best-effort — tombstone replacement is the real delete
    }

    return tombstone;
  }, [nostr, getSigner]);

  return { identity, publishProfile, publishHeartbeat, deleteListing };
}
